// Shared "should primary nav show" logic.
// Single source of truth for BottomNavigation/DesktopSidebar visibility AND
// for AppContainer's reserved layout space (sidebar padding-left, bottom-bar
// padding-bottom) so the two can never drift out of sync — e.g. reserving
// sidebar space on a page where the sidebar itself is hidden.

import { useLocation } from "react-router-dom";
// Shared auth state. useAuth() is a plain hook holding its own useState, so
// calling it directly would spin up another independent auth instance running
// its own async session check — the cause of the logged-out flash on the feed.
import { useAuthContext } from "../../providers";
import { useBottomNavigation } from "./useBottomNavigation";
import { authInterceptor } from "../../services/Authentication/authInterceptor";

// Pages where navigation should never show (public/unauthenticated pages)
const HIDE_NAV_PAGES = [
  "/",
  "/login",
  "/registration",
  "/onboarding",
  "/profile/setup",
  "/reset-password",
  "/privacy-policy",
  "/terms-of-service",
  "/v0-splash",
];

export const useShouldShowNav = (overrideVisible?: boolean): boolean => {
  const { user, isAuthenticated } = useAuthContext();
  const { isVisible: hookIsVisible } = useBottomNavigation();
  const location = useLocation();

  // Check for tokens synchronously - tokens are saved immediately on login
  const hasTokens = !!authInterceptor.getAccessToken();

  // Check if current page should hide navigation.
  // For '/', only match exact path. For others, match exact or nested paths.
  const shouldHideForRoute = HIDE_NAV_PAGES.some((page) => {
    if (page === "/") {
      return location.pathname === "/";
    }
    return location.pathname === page || location.pathname.startsWith(page + "/");
  });

  return (
    (hasTokens || (!!user && isAuthenticated)) &&
    !shouldHideForRoute &&
    overrideVisible !== false &&
    hookIsVisible !== false
  );
};
